/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  CalendarRestService,
  ConstructionScheduleRestService,
  MaintenanceScheduleRestService,
  PreConstructionScheduleRestService,
  SalesScheduleRestService,
  ScheduleTaskType,
  ScheduleType,
  TaskRelationType,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  getRouteQueryParam,
  getRouteStaticData,
  InitViewService,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { GanttChartLoader, GanttExtension } from '@gh/builder-shared-gantt-chart';
import { Observable } from 'rxjs';
import {
  ScheduleCombinedGanttDetails,
  scheduleCombinedGanttDetailsFromDto,
} from './schedule-combined-gantt-details.model';
import { FilterNames, FilterRestService } from '@gh/builder-shared';

type ScheduleRestService = ConstructionScheduleRestService
  | SalesScheduleRestService
  | PreConstructionScheduleRestService
  | MaintenanceScheduleRestService;

@Injectable()
export class ScheduleCombinedGanttDetailsBusinessService implements InitViewService {

  constructor(private gridLayouts: GridLayoutService,
              private dictionaries: DictionaryService,
              private calendarRest: CalendarRestService,
              private constructionScheduleRest: ConstructionScheduleRestService,
              private salesScheduleRest: SalesScheduleRestService,
              private preConstructionScheduleRest: PreConstructionScheduleRestService,
              private maintenanceScheduleRest: MaintenanceScheduleRestService,
              private filterService: FilterRestService,
              private ganttChartLoader: GanttChartLoader) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const scheduleIds = getRouteQueryParam(route, 'scheduleIds').split(',');
    const { type } = getRouteStaticData(route);

    return {
      gantt: this.ganttChartLoader.load({
        extensions: [
          GanttExtension.SmartRendering,
          GanttExtension.AutoScheduling,
          GanttExtension.Tooltip,
          GanttExtension.Marker,
        ],
      }),
      filter: this.filterService.get(FilterNames.ganttCombined),
      calendar: this.calendarRest.findAll(),
      details: this.get(type, scheduleIds),
      gridLayout: this.gridLayouts.get('scheduleCombinedGanttTasks'),
      taskTypeDict: this.dictionaries.getByEnum(ScheduleTaskType),
      relationshipTypeDict: this.dictionaries.getByEnum(TaskRelationType),
    };
  }

  get(type: ScheduleType, scheduleIds: ID[]): Observable<ScheduleCombinedGanttDetails> {
    return this.selectRestService(type).getCombined(scheduleIds).pipe(
      map$((schedules) => scheduleCombinedGanttDetailsFromDto(type, schedules)));
  }

  private selectRestService(type: ScheduleType): ScheduleRestService {
    return {
      [ScheduleType.Construction]: this.constructionScheduleRest,
      [ScheduleType.Sales]: this.salesScheduleRest,
      [ScheduleType.PreConstruction]: this.preConstructionScheduleRest,
      [ScheduleType.Maintenance]: this.maintenanceScheduleRest,
    }[type];
  }
}
